// TODO ISO 27001: migrar URL a variable de entorno cuando exista la pipeline.
// TODO ISO 27001: agregar trace-id a cada reconexión para correlación en auditoría.

import { useEffect, useRef, useState, useCallback } from "react";
import { log, sanitizeUrl } from "utils/logger";

function parseSSEEvent(rawEvent) {
    const event = { event: "message", data: "", id: null };
    const lines = rawEvent.split("\n");
    for (const line of lines) {
        if (line.startsWith("event:")) {
            event.event = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
            event.data += (event.data ? "\n" : "") + line.slice(5).trim();
        } else if (line.startsWith("id:")) {
            event.id = line.slice(3).trim();
        }
    }
    return event;
}

export function useSSE({
    url,
    getBody,
    getHeaders,
    onMessage,
    subscriptionKey,
    maxReconnectDelay = 30000,
    maxReconnectAttempts = 10,
}) {
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const [hasGivenUp, setHasGivenUp] = useState(false);

    const abortRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const cursorRef = useRef(null);

    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const getBodyRef = useRef(getBody);
    getBodyRef.current = getBody;

    const getHeadersRef = useRef(getHeaders);
    getHeadersRef.current = getHeaders;

    const connect = useCallback(async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (abortRef.current) {
            abortRef.current.abort();
        }
        abortRef.current = new AbortController();

        try {
            const userBody = (await getBodyRef.current?.()) || {};
            const userHeaders = (await getHeadersRef.current?.()) || {};

            const fullBody = { ...userBody };
            if (cursorRef.current) {
                fullBody.ultimoEventoId = cursorRef.current;
                log.debug("🔖 Reconectando con cursor");
            }

            log.debug("🔌 Conectando a:", sanitizeUrl(url));

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "text/event-stream",
                    "Content-Type": "application/json",
                    ...userHeaders,
                },
                credentials: "omit",
                body: JSON.stringify(fullBody),
                signal: abortRef.current.signal,
            });

            const contentType = response.headers.get("content-type") || "";

            if (response.status === 401 || response.status === 403) {
                throw new Error("No autorizado");
            }

            if (!response.ok || !contentType.includes("text/event-stream")) {
                log.error(`Stream rechazado [status=${response.status}, content-type=${contentType}]`);
                throw new Error(`Stream no disponible (${response.status})`);
            }

            if (!response.body) {
                throw new Error("Sin stream de respuesta");
            }

            setIsConnected(true);
            setError(null);
            reconnectAttemptsRef.current = 0;
            setReconnectAttempt(0);
            setHasGivenUp(false);
            log.info("✅ Conexión establecida, escuchando eventos...");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    log.debug("📪 El servidor cerró el stream");
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split("\n\n");
                buffer = events.pop() || "";

                for (const rawEvent of events) {
                    if (!rawEvent.trim()) continue;
                    if (rawEvent.startsWith(":")) {
                        log.debug("💓 Heartbeat");
                        continue;
                    }

                    const event = parseSSEEvent(rawEvent);

                    if (event.id) {
                        cursorRef.current = event.id;
                    }

                    if (event.data) {
                        try {
                            const data = JSON.parse(event.data);
                            onMessageRef.current?.(data, event);
                        } catch (e) {
                            onMessageRef.current?.(event.data, event);
                        }
                    }
                }
            }

            setIsConnected(false);
            scheduleReconnect();
        } catch (err) {
            if (err.name === "AbortError") {
                log.debug("🔴 Conexión cancelada manualmente");
                return;
            }
            log.error("Error en stream SSE:", err);
            setError(new Error("Error de conexión"));
            setIsConnected(false);

            if (err.message === "No autorizado") {
                log.warn("🚫 Error de auth, no se reconecta");
                return;
            }

            scheduleReconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, subscriptionKey]);

    const scheduleReconnect = useCallback(() => {
        const attempt = reconnectAttemptsRef.current;

        if (attempt >= maxReconnectAttempts) {
            log.warn(`🛑 Se alcanzó el máximo de ${maxReconnectAttempts} reintentos`);
            setHasGivenUp(true);
            return;
        }

        const delay = Math.min(1000 * Math.pow(2, attempt), maxReconnectDelay);
        reconnectAttemptsRef.current += 1;
        setReconnectAttempt(attempt + 1);

        log.debug(`⏳ Reconectando en ${delay}ms (intento ${attempt + 1}/${maxReconnectAttempts})`);

        reconnectTimeoutRef.current = setTimeout(() => {
            connect();
        }, delay);
    }, [connect, maxReconnectDelay, maxReconnectAttempts]);

    useEffect(() => {
        cursorRef.current = null;
        connect();

        return () => {
            log.debug("🔌 Desconectando (cleanup)");
            if (abortRef.current) abortRef.current.abort();
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
    }, [connect]);

    const disconnect = useCallback(() => {
        log.debug("🔌 Desconexión manual");
        if (abortRef.current) abortRef.current.abort();
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectAttemptsRef.current = 0;
        setReconnectAttempt(0);
        cursorRef.current = null;
        setIsConnected(false);
    }, []);

    const reconnect = useCallback(() => {
        log.debug("🔄 Reconexión manual");
        reconnectAttemptsRef.current = 0;
        setReconnectAttempt(0);
        setHasGivenUp(false);
        connect();
    }, [connect]);

    return { isConnected, error, reconnectAttempt, hasGivenUp, disconnect, reconnect };
}
